import React, { useState, useEffect, useCallback, useRef } from "react";
import SearchComponent from "../../../../../../../elements/v2/SearchComponent";
import { API } from "aws-amplify";
import { useStore } from "../../../../../../../hooks";
import { getCustomerMessages } from "../../../../../../../graphql/queries";

const TimelineSearch = () => {
  const { authStore, unifiedCustomerProfileStore, assistanceUCPStore } =
    useStore();
  const [searchTerm, setSearchTerm] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [messages, setMessages] = useState([]);
  const [totalMatches, setTotalMatches] = useState(0);
  const [currentResultIndex, setCurrentResultIndex] = useState(0);
  const [, setError] = useState(null);

  const hasResults = useRef(false);

  const contactInfo = unifiedCustomerProfileStore.contactInfo[0] || {};

  useEffect(() => {
    if (searchTerm.trim() && searchTerm.length >= 3) {
      fetchMessages();
    } else if (searchTerm.trim() === "") {
      hasResults.current = false;
      setMessages([]);
      setTotalMatches(0);
      setCurrentResultIndex(0);
    } else if (searchTerm.length > 0 && searchTerm.length < 3) {
      setIsLoading(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm]);

  useEffect(() => {
    if (messages.length > 0) {
      hasResults.current = true;
      setCurrentResultIndex(0);
      
      setTimeout(() => {
        const timelineContainer = document.querySelector('.timeline-wrapper');
        if (timelineContainer) {
          const highlightedSpans = Array.from(
            timelineContainer.querySelectorAll('.highlighted-text')
          );
          if (highlightedSpans.length > 0) {
            scrollToHighlight(highlightedSpans[0]);
          }
        }
      }, 100);
    } else {
      hasResults.current = false;
    }
  }, [messages]);

  const clearSearchState = useCallback(async () => {
    setSearchTerm("");
    hasResults.current = false;
    setMessages([]);
    setTotalMatches(0);
    setCurrentResultIndex(0);
    await fetchAllMessages();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchAllMessages = async () => {
    if (!authStore.selectedAccount?.id || !contactInfo.contact_id) return;

    setIsLoading(true);
    setError(null);

    try {
      const input = {
        customer_id: authStore.selectedAccount.id,
        contact_id: contactInfo.contact_id,
      };

      const response = await API.graphql({
        query: getCustomerMessages,
        variables: { input },
        authMode: "AMAZON_COGNITO_USER_POOLS",
      });

      if (response.data?.getCustomerMessages?.statusCode === "200") {
        const responseBody = JSON.parse(response.data.getCustomerMessages.body);
        const allMessages = responseBody.messages || [];

        // Procesar los mensajes para mantener consistencia con el formato
        const processedMessages = allMessages.map((msg) => ({
          ...msg,
          message: msg.message?.replace(/\\\\"/g, '"').replace(/\\"/g, '"'),
          matches: []
        }));

        assistanceUCPStore.setTimelineMessages(processedMessages);
        setMessages([]);
        setTotalMatches(0);
      } else {
        setError("Error al obtener mensajes");
        console.error("Error response:", response);
      }
    } catch (err) {
      setError("Error al realizar la búsqueda");
      console.error("Error fetching messages:", err);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchMessages = useCallback(async () => {
    if (!authStore.selectedAccount?.id || !contactInfo.contact_id) return;

    setIsLoading(true);
    setError(null);

    try {
      const input = {
        customer_id: authStore.selectedAccount.id,
        contact_id: contactInfo.contact_id,
        search: searchTerm,
      };

      const response = await API.graphql({
        query: getCustomerMessages,
        variables: { input },
        authMode: "AMAZON_COGNITO_USER_POOLS",
      });

      if (response.data?.getCustomerMessages?.statusCode === "200") {
        const responseBody = JSON.parse(response.data.getCustomerMessages.body);

        const allMessages = responseBody.messages || [];

        // Procesar los mensajes manteniendo sus matches específicos
        const processedMessages = allMessages.map((msg) => ({
          ...msg,
          message: msg.message?.replace(/\\\\"/g, '"').replace(/\\"/g, '"'),
        }));

        const messagesWithMatches = processedMessages.filter(
          (msg) => msg.matches && msg.matches.length > 0
        );

        const totalMatchesCount = messagesWithMatches.reduce((total, msg) => {
          return total + (msg.matches?.length || 0);
        }, 0);

        assistanceUCPStore.setTimelineMessages(processedMessages);

        setMessages(messagesWithMatches);
        setTotalMatches(totalMatchesCount);

        if (messagesWithMatches.length > 0) {
          console.log("Estableciendo índice FORZOSAMENTE en 1");
          hasResults.current = true;
          setCurrentResultIndex(0);
        } else {
          hasResults.current = false;
        }

        console.log(
          `Encontrados ${messagesWithMatches.length} mensajes con ${totalMatchesCount} coincidencias`
        );
      } else {
        setError("Error al obtener mensajes");
        console.error("Error response:", response);
      }
    } catch (err) {
      setError("Error al realizar la búsqueda");
      console.error("Error fetching messages:", err);
    } finally {
      setIsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, authStore.selectedAccount?.id, contactInfo.contact_id]);

  const safeSetCurrentIndex = (index) => {
    if (hasResults.current) {
      setCurrentResultIndex(Math.max(0, Math.min(index, totalMatches - 1)));
    } else {
      setCurrentResultIndex(index);
    }
  };

  const handleSearch = (value) => {
    setSearchTerm(value);
  };

  const handleClearSearch = () => {
    setSearchTerm("");
    hasResults.current = false;
    setMessages([]);
    setTotalMatches(0);
    setCurrentResultIndex(0);
    fetchAllMessages();
  };

  const handleCancel = () => clearSearchState();

  const scrollToHighlight = (element) => {
    if (!element) return;

    const timelineContainer = document.querySelector(".timeline-wrapper");
    if (!timelineContainer) return;

    const elementRect = element.getBoundingClientRect();
    const containerRect = timelineContainer.getBoundingClientRect();
    const scrollTop =
      timelineContainer.scrollTop +
      (elementRect.top - containerRect.top) -
      containerRect.height / 2 +
      elementRect.height / 2;

    timelineContainer.scrollTop = scrollTop;
  };

  const navigateNext = () => {
    const timelineContainer = document.querySelector(".timeline-wrapper");
    if (timelineContainer) {
      const highlightedSpans = Array.from(
        timelineContainer.querySelectorAll(".highlighted-text")
      );
      if (
        highlightedSpans.length > 0 &&
        currentResultIndex < totalMatches - 1
      ) {
        const nextIndex = currentResultIndex + 1;
        safeSetCurrentIndex(nextIndex);
        scrollToHighlight(highlightedSpans[nextIndex]);
      }
    }
  };

  const navigatePrevious = () => {
    const timelineContainer = document.querySelector(".timeline-wrapper");
    if (timelineContainer) {
      const highlightedSpans = Array.from(
        timelineContainer.querySelectorAll(".highlighted-text")
      );
      if (highlightedSpans.length > 0 && currentResultIndex > 0) {
        const prevIndex = currentResultIndex - 1;
        safeSetCurrentIndex(prevIndex);
        scrollToHighlight(highlightedSpans[prevIndex]);
      }
    }
  };

  return (
    <SearchComponent
      searchTerm={searchTerm}
      onSearchChange={handleSearch}
      onClear={handleClearSearch}
      onCancel={handleCancel}
      onNavigateNext={navigateNext}
      onNavigatePrevious={navigatePrevious}
      filteredCount={totalMatches}
      totalCount={totalMatches}
      currentResultIndex={currentResultIndex}
      isLoading={isLoading}
      placeholder="Search messages..."
      minLength={3}
      debounceDelay={1000}
    />
  );
};

export default TimelineSearch;
